import React from "react";
import { useNavigate } from "react-router-dom";
import { useApplicationData } from "contexts/ApplicationDataContext";
import { FumigationRecord } from "contexts/applicationDataFieldTypes";
import HeaderSection from "components/HeaderSection/HeaderSection.component";
import useSubmitFumigationApplication from "hooks/useSubmitFumigationApplication";
import {
    buildFumigationSubmissionPayload,
    combineSchedule,
    formatPhoneNumber,
    isFilledRecord,
} from "services/fumigationSubmissionPayload";

// ── Shared styles ─────────────────────────────────────────────────────────────

const FONT: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: 'Arial, Helvetica, sans-serif',
};

const LABEL_TD: React.CSSProperties = {
    ...FONT, color: 'blue', textAlign: 'right', whiteSpace: 'nowrap', paddingRight: '4px', verticalAlign: 'top',
};

const COLON_TD: React.CSSProperties = {
    ...FONT, color: 'blue', paddingRight: '6px', verticalAlign: 'top',
};

const VALUE_TD: React.CSSProperties = {
    ...FONT, textAlign: 'left', verticalAlign: 'top',
};

const TH_STYLE: React.CSSProperties = {
    ...FONT, color: 'white', backgroundColor: '#444444', textAlign: 'left',
    padding: '4px 6px', fontWeight: 'bold', whiteSpace: 'nowrap',
};

const DATA_TD: React.CSSProperties = {
    ...FONT, padding: '4px 6px', verticalAlign: 'top',
};

const BTN_STYLE: React.CSSProperties = {
    ...FONT, padding: '4px 16px', cursor: 'pointer', marginRight: '8px',
};

// ── Component ─────────────────────────────────────────────────────────────────

const ReviewAndSubmitPage: React.FC = () => {
    const navigate = useNavigate();
    const { applicationData } = useApplicationData();
    const { mutate: submitApplication, isLoading, isSuccess, isError } =
        useSubmitFumigationApplication();

    // Only show records that have data — same rule the payload builder applies
    const filledRecords: FumigationRecord[] = (applicationData.records || []).filter(isFilledRecord);

    const handleConfirm = () => {
        const payload = buildFumigationSubmissionPayload(applicationData);
        submitApplication(payload);
    };

    const handleEdit = () => {
        navigate('/');
    };

    return (
        <>
            <HeaderSection sections="Review and Submit" />

            <div style={{ padding: '16px 90px' }}>

                {/* ── Submit status ─────────────────────── */}
                <div aria-live="polite" role="status">
                    {isSuccess && (
                        <p style={{ ...FONT, fontSize: '16px', color: 'green', fontWeight: 'bold', marginBottom: '16px' }}>
                            You have successfully submitted your fumigation request - Please print this page for your records
                        </p>
                    )}
                    {isError && (
                        <p style={{ ...FONT, fontSize: '16px', color: 'red', fontWeight: 'bold', marginBottom: '16px' }}>
                            We could not submit your application. Please try again.
                        </p>
                    )}
                </div>

                {/* ── Company / Contact Info ─────────────── */}
                <table style={{ borderCollapse: 'collapse', marginBottom: '16px' }} cellPadding={3}>
                    <tbody>
                        <tr><td style={LABEL_TD}>Fumigation Company Name</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{applicationData.fumigationCompanyName}</td></tr>
                        <tr><td style={LABEL_TD}>Contact Person</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{applicationData.contactPerson}</td></tr>
                        <tr><td style={LABEL_TD}>Telephone Number</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{formatPhoneNumber(applicationData.telephoneAreaCode, applicationData.telephoneMid3, applicationData.telephoneLast4, applicationData.telephoneExtension)}</td></tr>
                        <tr><td style={LABEL_TD}>Email Address</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{applicationData.emailAddress}</td></tr>
                        <tr><td style={LABEL_TD}>Alternate Contact</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{applicationData.alternateContact}</td></tr>
                        <tr><td style={LABEL_TD}>Alternate Telephone Number</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{formatPhoneNumber(applicationData.alternateAreaCode, applicationData.alternateMid3, applicationData.alternateLast4, applicationData.alternateExtension)}</td></tr>
                        <tr><td style={LABEL_TD}>Alternate Email Address</td><td style={COLON_TD}>:</td><td style={VALUE_TD}>{applicationData.alternateEmailAddress}</td></tr>
                    </tbody>
                </table>

                {/* ── Records table ─────────────────────── */}
                {filledRecords.length > 0 && (
                    <table style={{ borderCollapse: 'collapse', width: '100%', marginBottom: '16px' }} cellPadding={4} cellSpacing={0}>
                        <thead>
                            <tr>
                                <th style={TH_STYLE}>Customer Name</th>
                                <th style={TH_STYLE}>Fumigation Premise Address</th>
                                <th style={TH_STYLE}>Order<br />Type</th>
                                <th style={TH_STYLE}>Close Schedule</th>
                                <th style={TH_STYLE}>Restore Schedule</th>
                                <th style={{ ...TH_STYLE, textAlign: 'right' }}>Total<br />Meter</th>
                                <th style={TH_STYLE}>Access Arrangements</th>
                                <th style={TH_STYLE}>Code Type</th>
                                <th style={TH_STYLE}>Code</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filledRecords.map((row, i) => (
                                <tr key={row.id ?? i} style={{ backgroundColor: i % 2 === 0 ? '#fff' : '#f5f5f5' }}>
                                    <td style={DATA_TD}>{row.customerName}</td>
                                    <td style={DATA_TD}>{row.premiseAddress}</td>
                                    <td style={DATA_TD}>{row.aer}</td>
                                    <td style={DATA_TD}>{combineSchedule(row.closeSchedule, row.closeTime)}</td>
                                    <td style={DATA_TD}>{combineSchedule(row.restoreSchedule, row.restoreTime)}</td>
                                    <td style={{ ...DATA_TD, textAlign: 'right' }}>{row.qty}</td>
                                    <td style={DATA_TD}>{row.accessArrangements !== 'None' ? row.accessArrangements : ''}</td>
                                    <td style={DATA_TD}>{row.codeType !== 'None' ? row.codeType : ''}</td>
                                    <td style={DATA_TD}>{row.code}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {/* ── Additional Access Info ────────────── */}
                <table style={{ borderCollapse: 'collapse', marginBottom: '24px' }}>
                    <tbody>
                        <tr>
                            <td style={LABEL_TD}>Additional Access / Address Information</td>
                            <td style={COLON_TD}>:</td>
                            <td style={VALUE_TD}>{applicationData.additionalAccessInformation}</td>
                        </tr>
                    </tbody>
                </table>

                {/* ── Confirm / Edit buttons — hidden once submitted ────── */}
                {!isSuccess && (
                    <div style={{ marginTop: '16px' }}>
                        <button
                            type="button"
                            style={{ ...BTN_STYLE, cursor: isLoading ? 'not-allowed' : 'pointer' }}
                            onClick={handleConfirm}
                            disabled={isLoading}
                        >
                            {isLoading ? 'Submitting...' : 'Confirm'}
                        </button>
                        <button type="button" style={BTN_STYLE} onClick={handleEdit} disabled={isLoading}>
                            Edit
                        </button>
                    </div>
                )}
            </div>
        </>
    );
};

export default ReviewAndSubmitPage;
